import { ArrowUpRight, Sparkles, Star } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatMoney, planPrice } from '../lib/api'
import type { Product } from '../types'
import { ProductImage } from './ProductImage'
import { StatusBadge } from './StatusBadge'

function ProductCard({ product }: { product: Product }) {
  const offered = product.plans.length > 0 ? product.plans : (['monthly'] as const)
  const fromPrice = Math.min(...offered.map((plan) => planPrice(product, plan)))
  const lifetimeOnly = offered.length === 1 && offered[0] === 'lifetime'
  const stockState = product.stock < 1 ? 'out' : product.stock <= 10 ? 'low' : 'ok'

  return (
    <Link
      className="pcard"
      to={`/p/${product.slug}`}
      style={{ ['--pcard-accent' as string]: product.accent }}
      aria-label={`View ${product.name} details`}
    >
      <div className="pcard__visual">
        <span className="pcard__orbit" aria-hidden="true" />
        <span className="pcard__halo" aria-hidden="true" />
        <ProductImage name={product.name} />
        {product.tag ? <span className="pcard__tag">{product.tag}</span> : null}
        <span className="stock-chip pcard__stock" data-stock={stockState}>
          {stockState === 'out' ? 'Out of stock' : stockState === 'low' ? `Only ${product.stock} left` : 'In stock'}
        </span>
      </div>

      <div className="pcard__body">
        <div className="pcard__title"><h3>{product.name}</h3><StatusBadge status={product.weao} /></div>
        <p>{product.tagline}</p>

        <div className="pcard__meta">
          {product.reviewsCount > 0 ? (
            <span className="pcard__rating" aria-label={`Rated ${product.rating} out of 5 from ${product.reviewsCount} reviews`}>
              <Star size={12} fill="currentColor" /> {product.rating.toFixed(1)}
              <small>({product.reviewsCount})</small>
            </span>
          ) : (
            <span className="pcard__rating pcard__rating--new"><Sparkles size={12} /> New</span>
          )}
          <span className="pcard__price">
            {lifetimeOnly ? '' : 'From '}<strong>{formatMoney(fromPrice, product.pricing.currency)}</strong>{lifetimeOnly ? <small> lifetime</small> : ''}
          </span>
        </div>

        <span className="pcard__cta">
          View details <ArrowUpRight size={15} />
        </span>
      </div>
    </Link>
  )
}

export function ProductGrid({ products }: { products: Product[] }) {
  return (
    <>
      <div className="section-heading">
        <div>
          <span className="eyebrow"><span>01</span> CATALOG</span>
          <h2>Pick your <em>edge.</em></h2>
        </div>
        <p>Every product is a one-time 1-week or 1-month license, delivered to your inbox the moment payment clears.</p>
      </div>
      <div className="pcard-grid">
        {products.map((product) => (
          <ProductCard product={product} key={product.id} />
        ))}
      </div>
    </>
  )
}
